import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useFetch } from "../../../hooks/api";
import { Row, Col, Table } from "react-bootstrap";
import moment from "moment";
import Loading from "../../core/blocks/Loading";
import { formatCurrency, formatPercent, formatNumber } from "../../../utils/formatNumber";
import { useDateFormatter } from "../../../i18n/useDateFormatter";
import { MarginCRProfilingForm } from "./templates/pricing/MarginCRProfiling";

const LogAction = ({ log, marketPlace }) => {
  const { t } = useTranslation(["programs"]);
  const action = log?.meta[2]?.function !== undefined ? log?.meta[2] : log?.meta[1];
  if (!action?.function) {
    return (
      <div>
        <em>{t(($) => $.programs.log["We did not perform any actions."])}</em>
      </div>
    );
  }

  return (
    <div>
      <div>
        <span className="">{t(($) => $.programs.log["The action that was performed was:"])}</span>
        <span className="fw-bold ps-2">{action?.function}</span>
      </div>
      <div className="ps-3">
        <span>
          {t(($) => $.programs.log["The previous price was:"])}{" "}
          <span className="fw-bold">
            {formatCurrency(action?.current_price, {
              marketPlace: marketPlace.marketPlace,
            })}
          </span>
        </span>
      </div>
      <div className="ps-3">
        <span>
          {t(($) => $.programs.log["The new price was:"])}{" "}
          <span className="fw-bold">
            {formatCurrency(action?.next_price, {
              marketPlace: marketPlace.marketPlace,
            })}
          </span>
        </span>
      </div>
    </div>
  );
};

const LogMeta = ({ log }) => {
  const { t } = useTranslation(["programs"]);
  const metaValue = log?.meta[2]?.value !== undefined ? log?.meta[2] : log?.meta[1];
  if (!metaValue?.value) {
    return null;
  }
  if (metaValue?.value.length === 0) {
    return null;
  }

  if (metaValue?.value?.length > 0) {
    return (
      <div>
        {t(
          ($) =>
            $.programs.log[
              "Dynamic Pricing looked at the {key} sold from {fromDate} to {toDate} and these were the daily values:"
            ],
          {
            key: metaValue?.value[0].key,
            fromDate: metaValue?.value[0].from_date,
            toDate: metaValue?.value[0].to_date,
          },
        )}
        <span className="ps-2">{metaValue?.value[0].value.join(", ")}</span>.
      </div>
    );
  }

  return null;
};

const LogEntries = ({ log, fullLog }) => {
  const { t } = useTranslation(["programs"]);
  const logMatchRegex = /checking \d+?/m;

  const entries = [];
  let foundScope = false;
  let action = fullLog?.meta[2]?.function !== undefined ? fullLog?.meta[2] : fullLog?.meta[1];
  let isHit = false;
  action = action?.function;

  try {
    for (let i = 0; i < log.length; i++) {
      const line = log[i];
      if (line.startsWith("getting scope")) {
        if (!foundScope) {
          const parts = line.split(" ");
          entries.push(
            <>
              <div className="pt-2">
                {t(($) => $.programs.log["The last time we performed an action, it was on {date} - {days} days ago."], {
                  date: parts[6].replace(",", ""),
                  days: parts[11],
                })}
              </div>
              <div className="py-1">{t(($) => $.programs.log["The rules on this day were:"])}</div>
            </>,
          );
        }
        foundScope = true;
      } else if (line.startsWith("checking rule Rule")) {
        const f = line.match(/function='([^']+)/)[1];
        const operators = line.match(/operator='([^']+)/g);
        const values = line.match(/values=\[([^\]]+)/g);

        let daysRule = `${f} by ${values[2].replace("values=[", "")}% `;
        daysRule += `if units sold ${operators[0].replace(
          "operator='",
          "",
        )} ${values[0].replace("values=[", "")} days `;
        daysRule += `${operators[2].replace("operator='", "")} `;
        daysRule += `${values[1].replace("values=[", "").split(",")[2].trim()}`;

        isHit = false;
        if (daysRule.includes(action)) {
          isHit = true;
        }
        entries.push(
          <div className={`ps-3`}>
            <span>{daysRule}</span>
          </div>,
        );
      } else if (logMatchRegex.test(line)) {
        const parts = line.split("(");
        const logMatch = parts[0].trim();
        const logParts = logMatch.split(" ");

        if (logParts[2] === ">=") {
          entries.push(
            <div className={`ps-3 pb-3`}>
              {t(($) => $.programs.log["Did we sell more than or equal to {threshold} units?"], {
                threshold: logParts[3],
              })}
              {isHit ?
                <span className="fw-bold ps-2" style={{ color: "#007700" }}>
                  {t(($) => $.programs.log["Yes we did. We sold {units} units."], { units: logParts[1] })}
                </span>
              : <span className="fw-bold ps-2" style={{ color: "#880000" }}>
                  {t(($) => $.programs.log["No we did not."])}
                </span>
              }
            </div>,
          );
        } else if (logParts[2] === "<=") {
          entries.push(
            <div className={`ps-3 pb-3`}>
              {t(($) => $.programs.log["Did we less more than or equal to {threshold} units?"], {
                threshold: logParts[3],
              })}
              {isHit ?
                <span className="fw-bold ps-2" style={{ color: "#007700" }}>
                  {t(($) => $.programs.log["Yes we did. We sold {units} units."], { units: logParts[1] })}
                </span>
              : <span className="fw-bold ps-2" style={{ color: "#880000" }}>
                  {t(($) => $.programs.log["No we did not."])}
                </span>
              }
            </div>,
          );
        }
      }
    }
  } catch {
    // do nothing
  }
  return entries;
};

function parseLogEntries(logEntries, t) {
  return logEntries.map((entry) => {
    let result = "";

    if (entry.startsWith("checking rule")) {
      const conditionMatches = [...entry.matchAll(/Condition\(operator='(.*?)', values=\['\$\{(.*?)\}', ([0-9.]+)\]/g)];
      const actionMatch = entry.match(/Action\(function='(.*?)', operator='(.*?)', values=\[([0-9.]+)\]/);

      result += t(($) => $.programs.log["Checking rule with conditions:"]) + " ";
      result += conditionMatches.map((m) => `${m[2]} ${m[1]} ${m[3]}`).join(", ");

      if (actionMatch) {
        result += t(($) => $.programs.log[" → Action: {fn} ({op} {value}%)"], {
          fn: actionMatch[1],
          op: actionMatch[2],
          value: actionMatch[3],
        });
      } else {
        result += " " + t(($) => $.programs.log["→ No actions"]);
      }
    } else if (entry.startsWith("getting scope")) {
      const dateMatch = entry.match(/last action (.*?), days since last action ([0-9]+)/);
      if (dateMatch) {
        result += t(($) => $.programs.log["Scope checked since last action date ({date}), {days} day(s) ago"], {
          date: dateMatch[1],
          days: dateMatch[2],
        });
      }
    } else {
      result = entry;
    }

    return result;
  });
}

const CRLogDetails = ({ log, marketPlace, viewableLogId, setViewableLogId, decisionTableId, setDecisionTableId }) => {
  const { t } = useTranslation(["programs"]);
  const formState = {
    defaultValues: log.history,
  };

  return (
    <>
      <div className="pt-3">
        <LogAction log={log} marketPlace={marketPlace} />
      </div>
      <div className="py-1"></div>
      <Row className="py-3">
        <Col xs={2} className="text-center">
          <div
            style={{
              cursor: "pointer",
              border: "1px solid #999",
              backgroundColor: "#ddd",
              borderRadius: "5px",
              padding: "5px",
              width: "150px",
              textAlign: "center",
            }}
            onClick={() => {
              setViewableLogId(viewableLogId === log.id ? null : log.id);
            }}
          >
            {t(($) => $.programs.log["View Log Text"])}
          </div>
        </Col>
        <Col xs={2} className="text-center">
          <div
            style={{
              cursor: "pointer",
              border: "1px solid #999",
              backgroundColor: "#ddd",
              borderRadius: "5px",
              padding: "5px",
              width: "150px",
              textAlign: "center",
            }}
            onClick={() => {
              setDecisionTableId(decisionTableId === log.id ? null : log.id);
            }}
          >
            {t(($) => $.programs.log["View Decision Table"])}
          </div>
        </Col>
      </Row>

      <div className="py-1">
        {viewableLogId === log.id && (
          <div className="py-3">
            <Table striped bordered hover>
              <tbody>
                {parseLogEntries(log.meta[0].logs, t).map((logEntry, i) => (
                  <tr key={i}>
                    <td>{logEntry}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
        {decisionTableId === log.id && (
          <div className="py-3">
            <MarginCRProfilingForm formState={formState} />
          </div>
        )}
      </div>
    </>
  );
};

const Log = ({ log, marketPlace, viewableLogId, setViewableLogId, decisionTableId, setDecisionTableId }) => {
  const { t } = useTranslation(["programs"]);
  const dateFmt = useDateFormatter("medium");

  if (log.program.template_type === "MARGIN_PROFILING") {
    return (
      <div className="py-2 my-2">
        <div className="fw-bold p-3" style={{ border: "1px solid #555", backgroundColor: "#ddd" }}>
          {dateFmt.format(moment(log.ts).toDate())}
        </div>
        <div className="py-1">
          <LogEntries log={log?.meta[0]?.logs} fullLog={log} />
        </div>
        <div className="py-1">
          <LogMeta log={log} />
        </div>
        <div className="py-1">
          <LogAction log={log} marketPlace={marketPlace} />
        </div>
      </div>
    );
  } else if (log.program.template_type === "CR_UNITS") {
    if (log.meta.length <= 1 || log.meta[1].value.length === 0) {
      return (
        <div className="py-2 my-2">
          <div className="fw-bold p-3" style={{ border: "1px solid #555", backgroundColor: "#eee" }}>
            {dateFmt.format(moment(log.ts).toDate())}
          </div>
          <CRLogDetails
            log={log}
            marketPlace={marketPlace}
            viewableLogId={viewableLogId}
            setViewableLogId={setViewableLogId}
            decisionTableId={decisionTableId}
            setDecisionTableId={setDecisionTableId}
          />
        </div>
      );
    }

    const fromDate = moment(log.meta[1].value[0].from_date);
    const toDate = moment(log.meta[1].value[0].to_date);
    const diffDays = toDate.diff(fromDate, "days");
    const unitList = log.meta[1].value[1].value.slice(-diffDays);
    const unitSum = unitList.reduce((acc, unit) => acc + unit, 0);

    return (
      <div className="py-2 my-2">
        <div className="fw-bold p-3" style={{ border: "1px solid #555", backgroundColor: "#eee" }}>
          {dateFmt.format(moment(log.ts).toDate())}
        </div>
        <div className="py-1">
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>{t(($) => $.programs.log["Long Page Views"])}</th>
                <th>{t(($) => $.programs.log["Long Units"])}</th>
                <th>{t(($) => $.programs.log["Long CR"])}</th>
                <th>{t(($) => $.programs.log["Short Page Views"])}</th>
                <th>{t(($) => $.programs.log["Short Units"])}</th>
                <th>{t(($) => $.programs.log["Short CR"])}</th>
                <th>{t(($) => $.programs.log.Diff)}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="text-center">{formatNumber(log.meta[1].value[0].long_page_views)}</td>
                <td className="text-center">{formatNumber(log.meta[1].value[0].long_orders)}</td>
                <td className="text-center">{formatPercent(log.meta[1].value[0].long_ocr)}</td>
                <td className="text-center">{formatNumber(log.meta[1].value[0].short_page_views)}</td>
                <td className="text-center">{formatNumber(log.meta[1].value[0].short_orders)}</td>
                <td className="text-center">{formatPercent(log.meta[1].value[0].short_ocr)}</td>
                <td className="text-center">{formatPercent(log.meta[1].value[0].ocr)}</td>
              </tr>
            </tbody>

            <thead>
              <tr>
                <th>{t(($) => $.programs.log["From Date"])}</th>
                <th>{t(($) => $.programs.log["To Date"])}</th>
                <th>{t(($) => $.programs.log["Unit List"])}</th>
                <th>{t(($) => $.programs.log.Units)}</th>
                <th colSpan={3}>&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="text-center"> {dateFmt.format(moment(log.meta[1].value[1].from_date).toDate())}</td>
                <td className="text-center"> {dateFmt.format(moment(log.meta[1].value[1].to_date).toDate())}</td>
                <td className="text-center">
                  {unitList.map((unit, i) => (
                    <span key={i} className="px-1">
                      {unit}
                    </span>
                  ))}
                </td>
                <td className="text-center">{unitSum}</td>
                <td colSpan={3}>&nbsp;</td>
              </tr>
            </tbody>
          </Table>
        </div>
        <CRLogDetails
          log={log}
          marketPlace={marketPlace}
          viewableLogId={viewableLogId}
          setViewableLogId={setViewableLogId}
          decisionTableId={decisionTableId}
          setDecisionTableId={setDecisionTableId}
        />
      </div>
    );
  }

  return <span>{t(($) => $.programs.log["Unknown log type"])}</span>;
};

const ProgramLog = ({ programId, pricingPlanId, marketPlace, title }) => {
  const { t } = useTranslation(["programs"]);
  const [viewableLogId, setViewableLogId] = useState(null);
  const [decisionTableId, setDecisionTableId] = useState(null);

  const { data, isLoading } = useFetch(
    ["program_logs", programId, pricingPlanId],
    "/programslogs/",
    {
      program_id: programId,
      pricing_plan_id: pricingPlanId,
      only_action: true,
    },
    { select: (data) => data?.data?.results ?? [] },
  );

  return (
    <>
      <Row className="pt-5">
        <Col>
          <div className="pb-3">
            <span className="fs-4 fw-bold">{t(($) => $.programs.log["{title} Logs"], { title })}</span>
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          {isLoading && <Loading height={"300px"} fullPage={false} />}
          {!isLoading &&
            data.map((log, i) => (
              <div className="px-3 mb-2" style={{ border: "1px solid #111", borderRadius: "5px" }} key={i}>
                <Log
                  key={i}
                  log={log}
                  marketPlace={marketPlace}
                  viewableLogId={viewableLogId}
                  setViewableLogId={setViewableLogId}
                  decisionTableId={decisionTableId}
                  setDecisionTableId={setDecisionTableId}
                />
              </div>
            ))}
        </Col>
      </Row>
    </>
  );
};

export default ProgramLog;
